/**
 * Wire & Funding — shared tokens, money formatting and card/row primitives.
 *
 * ONE place for the tab's surface so the progress strip, the funding statement,
 * the net wire calculator and the wire ticket read as a single workspace rather
 * than three unrelated cards. Every color here is an alias into
 * `src/theme/colors.js` — nothing new is invented (see the color rule in
 * CLAUDE.md); this file only gives the roles tab-local names.
 *
 * Numbers use a system monospace stack so dollar columns line up down a narrow
 * column without a webfont dependency.
 */
import Box from "@mui/material/Box";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import colors, { slate, status } from "../../../../theme/colors";

export const T = {
  ink: colors.ink,
  ink2: slate[700],
  muted: slate[500],
  faint: slate[400],

  card: colors.white,
  line: slate[200],
  line2: slate[100],
  subtle: slate[50],

  accent: colors.action, // teal-600 — under white text / as a button fill
  accentInk: colors.tintFg, // teal-700 — active text
  accentTint: colors.teal50,
  accentTint2: colors.tint, // teal-100

  green: status.success.fg,
  greenTint: status.success.bg,
  amber: status.warning.fg,
  amberTint: status.warning.bg,
  red: status.error.fg,
  redTint: status.error.bg,
  steel: colors.steel,
  steelTint: colors.steelTint,

  radius: "12px",
  radiusSm: "8px",
  shadow: "0 1px 2px rgba(15,23,42,.04), 0 4px 14px rgba(15,23,42,.05)",
  mono: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
};

/** Placeholder for an absent value. */
export const EMPTY = "—";

/**
 * Dollars WITH cents. The shared `formatDollar` drops the decimals, and on a
 * wire tie-out the cents are the whole point — a $0.40 variance is the
 * difference between "balanced" and "out of balance". Tab-local on purpose.
 */
export const money = (v, fallback = EMPTY) => {
  if (v == null || v === "") return fallback;
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/** Same, but always carries an explicit sign (used on ledger lines). */
export const signedMoney = (v, fallback = EMPTY) => {
  if (v == null || v === "") return fallback;
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  if (n === 0) return money(0);
  return `${n > 0 ? "+" : "−"}${money(Math.abs(n))}`;
};

export const num = (v) => {
  if (v == null || v === "") return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** Percent from a whole-number rate (the statement stores 7.0 for 7%). */
export const ratePct = (v, fallback = EMPTY) => {
  if (v == null || v === "") return fallback;
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  // Rates under 1 are stored as fractions in a few places; treat them as such.
  const whole = Math.abs(n) <= 1 ? n * 100 : n;
  return `${Number(whole.toFixed(3))}%`;
};

/** Small uppercase caption used for every section header on the tab. */
export const labelSx = {
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: "0.07em",
  textTransform: "uppercase",
  color: T.faint,
  lineHeight: 1.3,
};

const TONES = {
  neutral: { bg: T.line2, fg: T.muted, border: T.line },
  accent: { bg: T.accentTint2, fg: T.accentInk, border: colors.tintBorder },
  info: { bg: T.steelTint, fg: T.steel, border: "#C3D2E6" },
  success: { bg: T.greenTint, fg: T.green, border: "#CBE8D6" },
  warning: { bg: T.amberTint, fg: T.amber, border: "#F2E2C4" },
  // `title` / `body` only matter on a red-background alert (Callout) — see
  // status.error in src/theme/colors.js.
  error: {
    bg: T.redTint,
    fg: T.red,
    border: "#F5C9C9",
    title: status.error.title,
    body: status.error.body,
  },
};

/**
 * The tab's one badge. MUI's `Chip` carries too much height for a dense card
 * column, so this is a plain span sized to sit inline with 12px text.
 */
export const Pill = ({ label, tone = "neutral", outlined = false, sx }) => {
  const t = TONES[tone] || TONES.neutral;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        flex: "none",
        px: 0.75,
        height: 19,
        borderRadius: "5px",
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: "0.02em",
        whiteSpace: "nowrap",
        bgcolor: outlined ? "transparent" : t.bg,
        color: t.fg,
        border: `1px solid ${outlined ? t.border : "transparent"}`,
        ...sx,
      }}
    >
      {label}
    </Box>
  );
};

/** Section caption + optional right-hand count, over a hairline. */
export const SectionHead = ({ label, end, sx }) => (
  <Flx
    jb
    ac
    gap={1}
    sx={{
      py: 0.75,
      borderBottom: `1px solid ${T.line}`,
      mb: 0.25,
      ...sx,
    }}
  >
    <Txt sx={labelSx}>{label}</Txt>
    {end ? <Txt sx={{ ...labelSx, color: T.muted }}>{end}</Txt> : null}
  </Flx>
);

/**
 * One statement / ledger line: label (+ optional category badge) on the left,
 * a monospace amount on the right, with the calc note underneath when the
 * backend explains how the number was reached.
 */
export const LineRow = ({
  label,
  badge,
  badgeTone = "neutral",
  amount,
  note,
  strong = false,
  tone,
  onClick,
}) => (
  <Flx
    column
    sx={{
      py: 0.85,
      borderBottom: `1px ${strong ? "solid" : "dashed"} ${strong ? T.line : T.line2}`,
      "&:last-of-type": { borderBottom: "none" },
      ...(onClick && { cursor: "pointer", "&:hover": { bgcolor: T.subtle } }),
    }}
    onClick={onClick}
  >
    <Flx jb gap={1.5} ac sx={{ minWidth: 0 }}>
      <Flx ac gap={0.75} sx={{ minWidth: 0 }}>
        <Txt
          sx={{
            fontSize: 12.5,
            fontWeight: strong ? 600 : 400,
            color: strong ? T.ink : T.ink2,
            lineHeight: 1.35,
          }}
        >
          {label}
        </Txt>
        {badge ? <Pill label={badge} tone={badgeTone} /> : null}
      </Flx>
      <Txt
        sx={{
          flex: "none",
          fontFamily: T.mono,
          fontSize: strong ? 13.5 : 12.5,
          fontWeight: strong ? 700 : 500,
          color: tone || (strong ? T.ink : T.ink2),
          letterSpacing: "-0.01em",
        }}
      >
        {amount}
      </Txt>
    </Flx>
    {note ? (
      <Txt sx={{ fontSize: 11, color: T.faint, mt: 0.25 }}>{note}</Txt>
    ) : null}
  </Flx>
);

/** Empty-state line inside a section. */
export const EmptyLine = ({ children }) => (
  <Txt
    sx={{
      fontSize: 12,
      color: T.faint,
      fontStyle: "italic",
      py: 1.5,
      textAlign: "center",
    }}
  >
    {children}
  </Txt>
);

/** Caption-over-value cell, for the terms/context strips. */
export const StatCell = ({ label, value, mono = false, tone, sub }) => (
  <Flx column gap={0.3} sx={{ minWidth: 0 }}>
    <Txt sx={{ ...labelSx, display: "block" }}>{label}</Txt>
    <Txt
      sx={{
        fontSize: 13,
        fontWeight: 600,
        color: tone || (value === EMPTY ? T.faint : T.ink),
        fontFamily: mono ? T.mono : undefined,
        lineHeight: 1.3,
        wordBreak: "break-word",
      }}
    >
      {value}
    </Txt>
    {sub ? (
      <Txt sx={{ fontSize: 11, color: T.faint, lineHeight: 1.3 }}>{sub}</Txt>
    ) : null}
  </Flx>
);

/** Responsive strip of StatCells — the "what is this loan" header on a pane. */
export const StatStrip = ({ children, min = 150, sx }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`,
      gap: 2,
      px: 2,
      py: 1.75,
      bgcolor: T.subtle,
      border: `1px solid ${T.line}`,
      borderRadius: T.radiusSm,
      ...sx,
    }}
  >
    {children}
  </Box>
);

/**
 * The one alert shape on the tab: an icon, a headline, an explanation and an
 * optional right-hand figure.
 */
export const Callout = ({ tone = "info", icon, title, body, figure, sx }) => {
  const t = TONES[tone] || TONES.neutral;
  return (
    <Flx
      jb
      gap={2}
      sx={{
        px: 1.75,
        py: 1.5,
        borderRadius: T.radiusSm,
        bgcolor: t.bg,
        border: `1px solid ${t.border}`,
        // Top-aligned so the icon marks the title line rather than floating
        // against the middle of a wrapped explanation.
        alignItems: "flex-start",
        ...sx,
      }}
    >
      <Flx gap={1.25} sx={{ alignItems: "flex-start", minWidth: 0 }}>
        {icon ? (
          <Box sx={{ color: t.fg, display: "flex", mt: "1px", flex: "none" }}>
            {icon}
          </Box>
        ) : null}
        <Flx column gap={0.25} sx={{ minWidth: 0 }}>
          <Txt sx={{ fontSize: 13, fontWeight: 700, color: t.title || t.fg }}>
            {title}
          </Txt>
          {body ? (
            <Txt
              sx={{ fontSize: 11.5, color: t.body || T.muted, lineHeight: 1.45 }}
            >
              {body}
            </Txt>
          ) : null}
        </Flx>
      </Flx>
      {figure != null ? (
        <Txt
          sx={{
            flex: "none",
            fontFamily: T.mono,
            fontSize: 18,
            fontWeight: 700,
            color: t.fg,
            letterSpacing: "-0.02em",
          }}
        >
          {figure}
        </Txt>
      ) : null}
    </Flx>
  );
};
